import * as Yup from "yup";
import { useState } from "react";
import { Form, Formik } from "formik";
import { Col, Row } from "react-bootstrap";
import { useDispatch } from "react-redux";
import styles from "../ReportIssues.module.scss";
import Button from "../../../../../common/Button/Button";
import Upload from "../../../../../common/Formik/Upload/Upload";
import Textarea from "../../../../../common/Formik/Textarea/Textarea";
import ErrorText from "../../../../../common/Formik/ErrorText/ErrorText";
import InputFormik from "../../../../../common/Formik/Input/InputFormik";
import { validationMessage } from "../../../../../../Helper/helperfunction";
import CommonDatePicker from "../../../../../common/Formik/DatePiker/DatePicker";
import { reportIssueAction } from "../../../../../../features/user/userSlice";
import { format } from "date-fns";

const blobToBase64 = (blob) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
};

const FailedTransaction = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const MAX_FILE_SIZE_MB = 2;
  const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;
  const SUPPORTED_FORMATS = [
    "image/jpg",
    "image/jpeg",
    "image/png",
    "application/pdf",
  ]

  const validationSchema = Yup.object({
    transactionId: Yup.string()
      .trim()
      .required("Transaction ID is required")
      .min(6, "Transaction ID must be at least 6 characters")
      .max(50, "Transaction ID must not exceed 50 characters"),

    amount: Yup.number()
      .typeError("Amount must be a valid number")
      .required("Amount is required")
      .positive("Amount must be greater than 0")
      .max(10000000, "Amount is too large"),

    date: Yup.date()
      .typeError("Invalid Transaction Date")
      .nullable()
      .required(validationMessage("Transaction Date"))
      .max(new Date(), "Transaction Date cannot be in the future"),


    MerchantName: Yup.string()
      .trim()
      .max(100, "Merchant name must not exceed 100 characters")
      .nullable(),

    description: Yup.string()
      .trim()
      .required("Description is required")
      .min(10, "Description must be at least 10 characters")
      .max(500, "Description must not exceed 500 characters"),

    profile_img: Yup.mixed()
      .nullable()
      .test(
        "fileExists",
        "Invalid file",
        (file) => !file || file instanceof File
      )

      //  Size validation
      .test(
        "fileSize",
        `File size must be less than ${MAX_FILE_SIZE_MB} MB`,
        (file) => !file || file.size <= MAX_FILE_SIZE
      )

      //  Type validation (MIME)
      .test(
        "fileType",
        "Only JPG, JPEG, PNG, or PDF files are allowed",
        (file) => !file || SUPPORTED_FORMATS.includes(file.type)
      )

      //  Extension safety (extra layer)
      .test(
        "fileExtension",
        "Invalid file extension",
        (file) => {
          if (!file) return true;
          const ext = file.name?.split(".").pop()?.toLowerCase();
          return ["jpg", "jpeg", "png", "pdf"].includes(ext);
        }
      ),

  });

  const reportSubmit = async (values, resetForm) => {
    try {
      setLoading(true);

      let attachments;

      if (values.profile_img) {
        const base64 = await blobToBase64(values.profile_img);
        attachments = [base64];
      }

      const payload = {
        amount: Number(values?.amount),
        description: values?.description,
        dispute_type: "failed_transaction",
        transaction_id: values?.transactionId,
        transaction_date: format(new Date(values?.date), "yyyy-MM-dd"),
        ...(values?.MerchantName && { merchant_name: values.MerchantName }),
        ...(attachments && { attachments }),
      };

      const res = await dispatch(reportIssueAction(payload));

      if (res?.status === 200 || res?.status === 201) {
        resetForm();
      }
    } catch (error) {
      console.log(error, "error");
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className={styles.report_issues_content}>
      <h5>Report Failed Transaction</h5>
      <div className={styles.report_issues_content_form}>
        <Formik
          initialValues={{
            amount: "",
            date: null,
            description: "",
            MerchantName: "",
            transactionId: "",
            profile_img: null,

          }}
          validationSchema={validationSchema}
          onSubmit={async (values, { resetForm }) => {
            reportSubmit(values, resetForm)
          }}
        >
          {(formik) => (
            <Form onSubmit={(e) => { e.preventDefault(); e.stopPropagation(); }}>
              <Row>
                <Col lg={4} md={6}>
                  <InputFormik
                    name="transactionId"
                    label="Transaction ID"
                    placeholder="Enter transaction ID"
                    className={styles.input}
                    {...formik.getFieldProps("transactionId")}
                    formik={formik}
                  />
                </Col>

                <Col lg={4} md={6}>
                  <InputFormik
                    name="amount"
                    label="Amount"
                    placeholder="Enter amount"
                    className={styles.input}
                    {...formik.getFieldProps("amount")}
                    formik={formik}
                  />
                </Col>

                <Col lg={4} md={6}>
                  <CommonDatePicker
                    label="Transaction Date"
                    name="date"
                    maxDate={new Date()}
                    value={formik.values.date}
                    placeholder="DD/MM/YYYY"
                    onChange={(date) => {
                      formik.setFieldValue("date", date);
                    }}
                    formik={formik}
                  />
                </Col>
                <Col lg={4} md={6}>
                  <InputFormik
                    name="MerchantName"
                    label="Merchant Name"
                    placeholder="Enter merchant name (optional)"
                    className={styles.input}
                    {...formik.getFieldProps("MerchantName")}
                    formik={formik}
                  />
                </Col>
                <Col lg={4}>
                  <Textarea
                    name="description"
                    label="Description"
                    placeholder="Describe your issue"
                    formik={formik}
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    className={styles.form_textarea}
                  />
                </Col>

                <Col lg={4}>
                  <Upload
                    name="profile_img"
                    label="Upload receipt"
                    formik={formik}
                    image={formik.values.profile_img}
                    setBlobImage={(file) => {
                      formik.setFieldValue("profile_img", file);
                      formik.setFieldTouched("profile_img", true);
                    }}
                  />

                  {formik.touched.profile_img && formik.errors.profile_img && (
                    <ErrorText>{formik.errors.profile_img}</ErrorText>
                  )}

                </Col>
              </Row>
              <div className={styles.form_btns}>
                <Button
                  type="button"
                  className={styles.form_btns_btn}
                  variant="bordered_grey"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  className={styles.form_btns_btn}
                  disabled={loading}
                  onClick={(e) => {
                    formik.handleSubmit(e);
                  }}
                >
                  {loading ? "Submitting..." : "Submit Issue"}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default FailedTransaction;
